import { useEffect, useRef, useState } from "react";
import { api, fmtDuration } from "@/lib/api";
import { moveItem, parsePosition } from "@/lib/reorder";
import { Plus, Trash, ArrowUp, ArrowDown, MagnifyingGlass, Clock, TextAa, CaretDown, DotsSixVertical } from "@phosphor-icons/react";

// Program-info corner positions — map directly to the backend program_info_position values.
const PI_POSITIONS = [
  { v: "top_left", label: "Top Left" }, { v: "top_right", label: "Top Right" },
  { v: "bottom_left", label: "Bottom Left" }, { v: "bottom_right", label: "Bottom Right" },
];

// Editable 1-based position; commits on Enter/blur, invalid input reverts without reordering.
function PositionInput({ index, length, onCommit }) {
  const [txt, setTxt] = useState(String(index + 1));
  const [bad, setBad] = useState(false);
  useEffect(() => { setTxt(String(index + 1)); }, [index, length]);
  const commit = () => {
    const to = parsePosition(txt, length);
    setTxt(String(index + 1));
    if (to === null) {
      if (txt.trim() !== String(index + 1)) { setBad(true); setTimeout(() => setBad(false), 1500); }
      return;
    }
    if (to !== index) onCommit(to);
  };
  return (
    <input data-testid={`playlist-position-${index}`} value={txt} inputMode="numeric" aria-label="Position"
      title={bad ? `Enter a whole number from 1 to ${length}` : "Type a new position and press Enter"}
      onChange={(e) => setTxt(e.target.value)} onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") { setTxt(String(index + 1)); setTimeout(() => e.target.blur(), 0); }
      }}
      onFocus={(e) => e.target.select()}
      className={`font-mono text-[10px] w-8 text-center bg-transparent border rounded-sm py-0.5 outline-none transition-colors duration-150 ${bad ? "border-red-500 text-red-400" : "border-transparent hover:border-[#262626] focus:border-[#3A86FF] text-[#737373]"}`} />
  );
}

// Reusable ordered playlist builder for Continuous Loop content.
export function ContentPicker({ value, onChange, listHeight }) {
  const [media, setMedia] = useState([]);
  const [q, setQ] = useState("");
  const [openPI, setOpenPI] = useState({});   // per-event: Program Information panel expanded
  const [dragFrom, setDragFrom] = useState(null);
  const [dropAt, setDropAt] = useState(null);  // insertion slot 0..length while dragging
  const listRef = useRef(null);
  const rowRefs = useRef({});

  useEffect(() => { api.get("/assets").then((r) => setMedia(r.data)); }, []);

  const add = (m) => onChange([...value, {
    event_type: "content", asset_id: m.id, title: m.title,
    duration_seconds: m.duration_seconds, thumbnail: m.thumbnail, hls_url: m.hls_url,
  }]);
  const remove = (i) => {
    setOpenPI((s) => Object.fromEntries(Object.entries(s).filter(([k, f]) => f && +k !== i).map(([k]) => [+k > i ? +k - 1 : +k, true])));
    onChange(value.filter((_, idx) => idx !== i));
  };
  // Merge program-info edits onto one event (program info is stored ON THE EVENT, not the asset).
  const updateEvent = (i, patch) => onChange(value.map((e, idx) => idx === i ? { ...e, ...patch } : e));
  const togglePI = (i) => setOpenPI((s) => ({ ...s, [i]: !s[i] }));
  // Single reorder path for arrows, typed positions and drag-and-drop; open Info panels follow their event.
  const reorder = (from, to) => {
    const next = moveItem(value, from, to);
    if (next === value) return;
    const flags = moveItem(value.map((_, k) => !!openPI[k]), from, to);
    setOpenPI(Object.fromEntries(flags.map((f, k) => [k, f]).filter(([, f]) => f)));
    onChange(next);
  };
  const move = (i, dir) => reorder(i, i + dir);
  const endDrag = () => { setDragFrom(null); setDropAt(null); };
  const onRowDragOver = (e, i) => {
    if (dragFrom === null) return;
    e.preventDefault();
    const r = e.currentTarget.getBoundingClientRect();
    setDropAt(e.clientY > r.top + r.height / 2 ? i + 1 : i);
  };
  const onListDragOver = (e) => {
    if (dragFrom === null || !listRef.current) return;
    e.preventDefault();
    const r = listRef.current.getBoundingClientRect();
    const edge = 64;   // scroll faster the closer the pointer is to the list edge
    if (e.clientY < r.top + edge) listRef.current.scrollTop -= Math.ceil(8 + 40 * (r.top + edge - e.clientY) / edge);
    else if (e.clientY > r.bottom - edge) listRef.current.scrollTop += Math.ceil(8 + 40 * (e.clientY - (r.bottom - edge)) / edge);
  };
  const onListDrop = (e) => {
    e.preventDefault();
    if (dragFrom !== null && dropAt !== null) reorder(dragFrom, dropAt > dragFrom ? dropAt - 1 : dropAt);
    endDrag();
  };
  const showLine = (slot) => dragFrom !== null && dropAt === slot && slot !== dragFrom && slot !== dragFrom + 1;
  const line = (slot) => showLine(slot) && <div data-testid="playlist-drop-indicator" className="h-0.5 bg-[#3A86FF] rounded-full shadow-[0_0_6px_#3A86FF]" />;
  const total = value.reduce((a, e) => a + (e.duration_seconds || 0), 0);
  const filtered = media.filter((m) => m.title.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373] font-bold">Playback Order ({value.length})</div>
          <div className="font-mono text-[10px] text-[#525252] flex items-center gap-1"><Clock size={11} /> {fmtDuration(total)}</div>
        </div>
        <div ref={listRef} data-testid="playlist-list" onDragOver={onListDragOver} onDrop={onListDrop}
          className={`space-y-2 ${listHeight || "max-h-72"} overflow-y-auto pr-1`}>
          {value.length === 0 && <div className="text-xs text-[#525252] py-6 text-center border border-dashed border-[#262626] rounded-sm">Select content from the library →</div>}
          {value.map((e, i) => (
            <div key={i}>
            {line(i)}
            <div data-testid={`playlist-item-${i}`} ref={(el) => { rowRefs.current[i] = el; }}
              onDragOver={(ev) => onRowDragOver(ev, i)}
              className={`bg-[#0A0A0A] border border-[#262626] rounded-sm transition-opacity duration-150 ${dragFrom === i ? "opacity-40" : ""}`}>
              <div className="flex items-center gap-2 p-2">
                <span data-testid={`playlist-drag-handle-${i}`} draggable title="Drag to reorder"
                  onDragStart={(ev) => {
                    ev.dataTransfer.effectAllowed = "move";
                    ev.dataTransfer.setData("text/plain", String(i));
                    if (rowRefs.current[i]) ev.dataTransfer.setDragImage(rowRefs.current[i], 16, 16);
                    setDragFrom(i);
                  }}
                  onDragEnd={endDrag}
                  className="cursor-grab active:cursor-grabbing text-[#525252] hover:text-white transition-colors duration-150"><DotsSixVertical size={14} /></span>
                <PositionInput index={i} length={value.length} onCommit={(to) => reorder(i, to)} />
                <img src={e.thumbnail} alt="" className="h-8 w-12 object-cover rounded-sm" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs truncate">{e.title}</div>
                  <div className="font-mono text-[10px] text-[#525252]">{fmtDuration(e.duration_seconds)}</div>
                </div>
                <button data-testid={`program-info-toggle-${i}`} onClick={() => togglePI(i)}
                  title="Program Information"
                  className={`flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider rounded-sm border px-1.5 py-0.5 transition-colors duration-150 ${e.program_info_enabled ? "text-emerald-500 bg-emerald-500/10 border-emerald-500/20" : "text-[#737373] border-[#262626] hover:text-white"}`}>
                  <TextAa size={12} /> Info <CaretDown size={10} className={`transition-transform duration-150 ${openPI[i] ? "rotate-180" : ""}`} />
                </button>
                <div className="flex flex-col">
                  <button data-testid={`move-up-${i}`} onClick={() => move(i, -1)} className="text-[#737373] hover:text-white transition-colors duration-150"><ArrowUp size={12} /></button>
                  <button data-testid={`move-down-${i}`} onClick={() => move(i, 1)} className="text-[#737373] hover:text-white transition-colors duration-150"><ArrowDown size={12} /></button>
                </div>
                <button data-testid={`playlist-remove-${i}`} onClick={() => remove(i)} className="text-[#737373] hover:text-red-500 p-1 transition-colors duration-150"><Trash size={13} /></button>
              </div>

              {openPI[i] && (
                <div data-testid={`program-info-panel-${i}`} className="border-t border-[#262626] px-3 py-3 space-y-3 bg-[#0d0d0d]">
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-[#737373] font-bold">Program Information</span>
                    <input type="checkbox" data-testid={`program-info-enabled-${i}`}
                      checked={!!e.program_info_enabled}
                      onChange={(ev) => updateEvent(i, { program_info_enabled: ev.target.checked })}
                      className="accent-emerald-500 h-3.5 w-3.5 cursor-pointer" />
                  </label>
                  <div>
                    <label className="block text-[9px] uppercase tracking-[0.15em] text-[#525252] mb-1 font-bold">Label</label>
                    <input data-testid={`program-info-label-${i}`} value={e.program_info_label || ""}
                      onChange={(ev) => updateEvent(i, { program_info_label: ev.target.value })}
                      placeholder="NOW PLAYING"
                      className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-2 py-1.5 text-xs outline-none transition-colors duration-150" />
                  </div>
                  <div>
                    <label className="block text-[9px] uppercase tracking-[0.15em] text-[#525252] mb-1 font-bold">Description</label>
                    <textarea data-testid={`program-info-description-${i}`} value={e.program_info_description || ""}
                      onChange={(ev) => updateEvent(i, { program_info_description: ev.target.value })} rows={2}
                      placeholder="Watch this title on demand on ReVine TV"
                      className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-2 py-1.5 text-xs outline-none transition-colors duration-150 resize-none" />
                  </div>
                  <div>
                    <label className="block text-[9px] uppercase tracking-[0.15em] text-[#525252] mb-1 font-bold">Position</label>
                    <select data-testid={`program-info-position-${i}`} value={e.program_info_position || "bottom_left"}
                      onChange={(ev) => updateEvent(i, { program_info_position: ev.target.value })}
                      className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-2 py-1.5 text-xs outline-none transition-colors duration-150">
                      {PI_POSITIONS.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
                    </select>
                  </div>
                  <p className="text-[9px] text-[#525252] leading-snug">Title is taken automatically from this event ("{e.title}"). Independent of channel logo branding.</p>
                </div>
              )}
            </div>
            {i === value.length - 1 && line(value.length)}
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373] font-bold mb-2">ReVine Media Library</div>
        <div className="relative mb-2">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#525252]" />
          <input data-testid="content-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
            className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm pl-8 pr-3 py-1.5 text-sm outline-none transition-colors duration-150" />
        </div>
        <div className={`space-y-2 ${listHeight || "max-h-64"} overflow-y-auto pr-1`}>
          {filtered.map((m) => (
            <button key={m.id} data-testid={`content-add-${m.id}`} onClick={() => add(m)}
              className="w-full flex items-center gap-2 bg-[#0A0A0A] border border-[#262626] hover:border-[#3A86FF] rounded-sm p-2 text-left transition-colors duration-150 group">
              <img src={m.thumbnail} alt="" className="h-9 w-14 object-cover rounded-sm" />
              <div className="flex-1 min-w-0">
                <div className="text-xs truncate">{m.title}</div>
                <div className="font-mono text-[10px] text-[#525252]">{m.category} · {fmtDuration(m.duration_seconds)}</div>
              </div>
              <Plus size={16} className="text-[#525252] group-hover:text-[#3A86FF] transition-colors duration-150" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
